import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import type { ClientEnv } from 'constants/Chat.constants';
import type { CallSource } from 'constants/PlgPendoEvents.constants';
import {
  setClientEnv,
  setConversationId,
} from 'ducks/callPanel/callPanelSlice';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { v4 as uuid } from 'uuid';

import { ClientEnvironment } from '@polyai/common/constants/global.constants';

import { CALL_WS_ENDPOINT, WEBRTC_SIGNAL_WS_ENDPOINT } from 'api/endpoints';
import useMediaRecorder from 'components/organisms/CallPanel/hooks/useMediaRecorder';
import usePlayAudio from 'components/organisms/CallPanel/hooks/usePlayAudio';
import useRemoteAudio from 'components/organisms/CallPanel/hooks/useRemoteAudio';
import { useAppSelector, useLocalDispatch } from 'hooks/reduxHooks';
import { useIsFeatureOn } from 'hooks/useFeatureSwitch/useIsFeatureOn';
import useTelephony from 'hooks/useTelephony';
import { useToast } from 'hooks/useToast';
import useWebRTC from 'hooks/useWebRTC';
import { trackPendoEvent } from 'lib/pendo';
import type {
  ConnectedEvent,
  MarkEvent,
  MediaEvent,
  StartEvent,
  StreamEvent,
} from './types';
import { StreamEventType } from './types';
import type {
  CloseMessage,
  IceCandidateMessage,
  OfferMessage,
  SignalingMessage,
} from './webrtc-types';
import { SignalingMessageType, WEBRTC_ERROR_MESSAGES } from './webrtc-types';

const getStreamSID = () => `STREAM-${uuid()}`;
const getCallSID = () => `LOCAL-${uuid()}`;

const buildOfferAuth = (
  draftCallParams: DraftCallParams | null | undefined,
  connectionToken: string | undefined,
): Partial<OfferMessage> =>
  draftCallParams
    ? {
        authToken: draftCallParams.studioToken,
        accountId: draftCallParams.accountId,
        projectId: draftCallParams.projectId,
        agentVersionOverride: {
          artifactVersion: draftCallParams.artifactVersion,
          lambdaDeploymentVersion: draftCallParams.lambdaDeploymentVersion,
        },
      }
    : { authToken: connectionToken };

export type DraftCallParams = {
  accountId: string;
  projectId: string;
  artifactVersion: string;
  lambdaDeploymentVersion: string;
  studioToken: string;
};

export type UseCallOptions = {
  isEnabled?: boolean;
  variantName?: string | null;
  /** For draft calls: when set, use these instead of connection token */
  draftCallParams?: DraftCallParams | null;
  /** Distinguishes which surface initiated the call for Pendo tracking */
  source?: CallSource;
};

const useCall = (options?: UseCallOptions) => {
  const { isEnabled, draftCallParams, source } = options ?? {};
  const { isFeatureOn } = useIsFeatureOn();
  const isWebRTC = isFeatureOn('webrtc_calling');

  // --- Shared state ---
  const [isSocketOpen, setIsSocketOpen] = useState(false);
  const callSIDRef = useRef<string | null>(null);
  const callStartTimeRef = useRef<number | null>(null);
  const callAgentIdRef = useRef<string>('');

  const dispatch = useLocalDispatch();
  const toast = useToast();
  const { allConnectionTokens } = useTelephony();
  const clientEnv = useAppSelector(state => state.callPanel.clientEnv);
  const connectionToken = useMemo(() => {
    if (!allConnectionTokens) {
      return;
    }
    switch (clientEnv) {
      case ClientEnvironment.SANDBOX:
        return allConnectionTokens.sandbox?.token;
      case ClientEnvironment.PRE_RELEASE:
        return allConnectionTokens['pre-release']?.token;
      case ClientEnvironment.LIVE:
        return allConnectionTokens.live?.token;
    }
  }, [clientEnv, allConnectionTokens]);
  const { user } = useAuth0();
  const callerEmail = user?.email;

  // --- Old (bidirectional media) path state ---
  const [isAgentSpeakingOld, setIsAgentSpeakingOld] = useState(false);
  const sequenceNumberRef = useRef<number>(0);
  const streamSIDRef = useRef<string | null>(null);
  const marksRef = useRef<Record<string, string>>({});
  const lastMediaIdRef = useRef<string | null | undefined>(undefined);

  // --- Draft call tracking (used to derive deploying state) ---
  const [isDraftCall, setIsDraftCall] = useState(false);
  const [hasAgentSpoken, setHasAgentSpoken] = useState(false);

  // --- WebRTC path state ---
  const sessionIdRef = useRef<string>('');
  const iceCandidateQueueRef = useRef<RTCIceCandidate[]>([]);
  // Ref to send WS messages from callbacks that can't depend on sendMessage directly
  const sendWsMessageRef = useRef<(msg: string) => void>(() => {});
  const mediaStreamRef = useRef<MediaStream | null>(null);
  // Ref to track call ready state synchronously (set to OPEN after call setup, CLOSED on endCall)
  const readyStateRef = useRef<ReadyState>(ReadyState.CLOSED);

  // --- WebRTC hooks (always called per rules of hooks; only active when flag is on) ---
  const remoteAudio = useRemoteAudio();

  const isAgentSpeaking = isWebRTC
    ? remoteAudio.isAgentSpeaking
    : isAgentSpeakingOld;

  // Latch: once the agent speaks for the first time, deploying is done for this call
  useEffect(() => {
    if (isAgentSpeaking) {
      setHasAgentSpoken(true);
    }
  }, [isAgentSpeaking]);

  const peerConnection = useWebRTC({
    onRemoteStream: remoteAudio.setRemoteStream,
    onIceCandidate: (candidate: RTCIceCandidate) => {
      const sessionId = sessionIdRef.current;
      if (!sessionId) {
        iceCandidateQueueRef.current.push(candidate);
        return;
      }
      const msg: IceCandidateMessage = {
        type: SignalingMessageType.ICE_CANDIDATE,
        sessionId,
        data: candidate.toJSON(),
      };
      sendWsMessageRef.current(JSON.stringify(msg));
    },
    onConnectionStateChange: (state: RTCPeerConnectionState) => {
      if (state === 'failed' || state === 'disconnected') {
        endCall();
      }
    },
  });

  // --- Connection conditions ---
  const shouldStreamOld = Boolean(
    !isWebRTC &&
      isEnabled &&
      isSocketOpen &&
      (connectionToken || draftCallParams) &&
      callerEmail &&
      streamSIDRef.current &&
      callSIDRef.current,
  );

  const shouldConnectWebRTC = Boolean(
    isWebRTC &&
      isEnabled &&
      isSocketOpen &&
      (connectionToken || draftCallParams) &&
      callerEmail,
  );

  const shouldConnect = shouldStreamOld || shouldConnectWebRTC;

  // --- Old path helpers ---
  const getSequenceNumber = useCallback(() => {
    const number = sequenceNumberRef.current;
    sequenceNumberRef.current += 1;
    return number;
  }, []);

  // --- WS URL ---
  const webSocketUrlOld = useMemo(() => {
    if (!CALL_WS_ENDPOINT) return CALL_WS_ENDPOINT;
    const url = new URL(CALL_WS_ENDPOINT);
    if (connectionToken && !draftCallParams) {
      url.searchParams.set('auth-token', connectionToken);
    }
    return url.toString();
  }, [connectionToken, draftCallParams]);

  const webSocketUrl = isWebRTC ? WEBRTC_SIGNAL_WS_ENDPOINT : webSocketUrlOld;

  // --- WebRTC signaling handlers ---
  const webSocketOnOpenWebRTC = () => {
    const handleOpen = async () => {
      if (
        (!connectionToken && !draftCallParams) ||
        !callerEmail ||
        !callSIDRef.current
      ) {
        return;
      }
      try {
        const stream = mediaStreamRef.current;
        if (!stream) {
          toast.error({ title: 'No microphone stream available' });
          endCall();
          return;
        }
        const offer = await peerConnection.createOffer(stream);
        if (!offer.sdp) {
          toast.error({ title: 'Failed to create WebRTC offer' });
          endCall();
          return;
        }
        const offerMessage: OfferMessage = {
          type: SignalingMessageType.OFFER,
          sessionId: '',
          data: { type: 'offer', sdp: offer.sdp },
          callSid: callSIDRef.current,
          caller: callerEmail,
          mode: 'end-to-end',
          variantId: options?.variantName ?? undefined,
          ...buildOfferAuth(draftCallParams, connectionToken),
        };
        sendWsMessageRef.current(JSON.stringify(offerMessage));

        // Call is now ready
        readyStateRef.current = ReadyState.OPEN;
      } catch {
        toast.error({ title: 'Failed to start WebRTC call' });
        endCall();
      }
    };
    void handleOpen();
  };

  const webSocketOnMessageWebRTC = (event: MessageEvent<string>) => {
    try {
      const data = JSON.parse(event.data) as SignalingMessage;
      switch (data.type) {
        case SignalingMessageType.ANSWER: {
          if (!data.data?.sdp) {
            console.error('Invalid answer message: missing SDP');
            break;
          }
          sessionIdRef.current = data.sessionId;
          void peerConnection.setRemoteAnswer(data.data.sdp);
          // Flush queued ICE candidates now that we have a sessionId
          const queued = iceCandidateQueueRef.current;
          iceCandidateQueueRef.current = [];
          queued.forEach(candidate => {
            const msg: IceCandidateMessage = {
              type: SignalingMessageType.ICE_CANDIDATE,
              sessionId: data.sessionId,
              data: candidate.toJSON(),
            };
            sendWsMessageRef.current(JSON.stringify(msg));
          });
          break;
        }
        case SignalingMessageType.ICE_CANDIDATE: {
          if (!data.data?.candidate) {
            console.error('Invalid ICE candidate message: missing candidate');
            break;
          }
          void peerConnection.addIceCandidate(data.data);
          break;
        }
        case SignalingMessageType.ERROR: {
          toast.error({
            title: WEBRTC_ERROR_MESSAGES[data.code] ?? 'Call error',
          });
          endCall();
          break;
        }
        case SignalingMessageType.CLOSE: {
          endCall();
          break;
        }
      }
    } catch (error) {
      console.error('Error handling signaling message', error);
    }
  };

  // --- Old (bidirectional media) handlers ---
  const webSocketOnOpenOld = () => {
    const handleOpen = async () => {
      if (!streamSIDRef.current || !callSIDRef.current) {
        return;
      }

      // Restart recording to make sure the audio stream includes the header BE is expecting
      stopRecording();

      // Send connected event
      const connectedEvent: ConnectedEvent = {
        event: StreamEventType.CONNECTED,
        protocol: 'Call',
        version: '1.0',
      };
      sendMessage(JSON.stringify(connectedEvent));

      // Send start event - MUST be before startRecording so backend expects data before it arrives
      const startEvent: StartEvent = {
        event: StreamEventType.START,
        sequenceNumber: String(getSequenceNumber()),
        streamSid: streamSIDRef.current,
        start: {
          callSid: callSIDRef.current,
          mediaFormat: {
            encoding: 'audio/opus',
            sampleRate: 48000,
            channels: 1,
          },
          outboundMediaFormat: {
            encoding: 'audio/pcm32',
            sampleRate: 16000,
            channels: 1,
          },
          customParameters: {
            variantId: options?.variantName ?? '',
            ...(draftCallParams && {
              artifactVersion: draftCallParams.artifactVersion,
              lambdaDeploymentVersion: draftCallParams.lambdaDeploymentVersion,
              accountId: draftCallParams.accountId,
              projectId: draftCallParams.projectId,
            }),
          },
          caller: callerEmail!,
        },
      };
      sendMessage(JSON.stringify(startEvent));

      try {
        // IMPORTANT: Await recording start to ensure MediaRecorder is ready and will include
        // the WebM initialization segment (EBML header, codec info) in the first chunk.
        // Without this, the backend receives audio data without the required header and crashes.
        // Pass streamSID so the recorder's callbacks are tagged with it - only matching
        // streamSid chunks will be sent (stale callbacks from old recorder will be dropped).
        await startRecording(streamSIDRef.current);
      } catch {
        toast.error({
          title: 'Failed to start call',
        });
        endCall();
        return;
      }
    };
    void handleOpen();
  };

  const webSocketOnMessageOld = (event: MessageEvent<string>) => {
    const handleMessage = async () => {
      try {
        const data = JSON.parse(event.data) as StreamEvent;
        switch (data.event) {
          case StreamEventType.MEDIA: {
            // Play audio on media event packet receive
            const base64Audio = data.media.payload;
            lastMediaIdRef.current = await playAudio(base64Audio);

            if (!isAgentSpeakingOld) {
              setIsAgentSpeakingOld(true);
            }
            break;
          }
          case StreamEventType.MARK: {
            if (lastMediaIdRef.current) {
              marksRef.current[lastMediaIdRef.current] = data.mark.name;
            }
            break;
          }
          case StreamEventType.CLEAR: {
            // handle barge-in
            const playedBytes = stopAudioOld();
            sendMarkMessage(playedBytes);
            break;
          }
        }
      } catch (error) {
        console.error(
          'Error occurred. Unable to handle incoming message.',
          error,
        );
      }
    };
    void handleMessage();
  };

  // --- Unified WS handlers that delegate to the active path ---
  const webSocketOnOpen = () => {
    isWebRTC ? webSocketOnOpenWebRTC() : webSocketOnOpenOld();
  };

  const webSocketOnMessage = (event: MessageEvent<string>) => {
    isWebRTC ? webSocketOnMessageWebRTC(event) : webSocketOnMessageOld(event);
  };

  const webSocketOnError = () => {
    endCall();
  };

  const webSocketOnClose = () => {
    endCall();
  };

  const { sendMessage, readyState } = useWebSocket(
    webSocketUrl,
    {
      onOpen: webSocketOnOpen,
      onMessage: webSocketOnMessage,
      onError: webSocketOnError,
      onClose: webSocketOnClose,
    },
    shouldConnect,
  );

  // Keep sendMessage ref in sync for use in ICE candidate and endCall callbacks
  sendWsMessageRef.current = sendMessage;

  const isStreaming = readyState === ReadyState.OPEN;

  // --- Old path: mark handling and audio playback ---
  const handleSendMarkMessage = useCallback(
    (markName: string, playedBytes?: number | null) => {
      const markMessage: MarkEvent = {
        event: StreamEventType.MARK,
        mark: {
          name: markName,
          timestamp: Date.now(),
          ...(playedBytes !== undefined
            ? { playedBytes: playedBytes ?? null }
            : {}),
        },
      };
      sendMessage(JSON.stringify(markMessage));
    },
    [sendMessage],
  );

  const {
    playAudio,
    stopAudio: stopAudioOld,
    resetCurrentAudioSegmentStats,
  } = usePlayAudio({
    audioBufferSource: {
      onEnded: (_, audioBufferEntry) => {
        // send any marks
        const markName = marksRef.current[audioBufferEntry.id];
        if (markName === undefined) {
          return;
        }
        delete marksRef.current[audioBufferEntry.id];

        handleSendMarkMessage(markName);
        resetCurrentAudioSegmentStats();

        if (isAgentSpeakingOld) {
          setIsAgentSpeakingOld(false);
        }
      },
    },
  });

  // Send mark message with the number of played bytes in event of barge-in
  const sendMarkMessage = useCallback(
    (playedBytes?: number | null) => {
      if (lastMediaIdRef.current) {
        const markName = marksRef.current[lastMediaIdRef.current];
        if (markName === undefined) {
          return;
        }
        delete marksRef.current[lastMediaIdRef.current];
        handleSendMarkMessage(markName, playedBytes);
        resetCurrentAudioSegmentStats();

        if (isAgentSpeakingOld) {
          setIsAgentSpeakingOld(false);
        }
      }
    },
    [handleSendMarkMessage, resetCurrentAudioSegmentStats, isAgentSpeakingOld],
  );

  // --- Old path: base64 audio streaming via onDataAvailable ---
  const onDataAvailable = useCallback(
    (event: BlobEvent, streamSid: string | null) => {
      const handleData = async (event: BlobEvent, streamSid: string | null) => {
        if (!streamSid) {
          return;
        }

        try {
          // Convert the Blob to an ArrayBuffer
          const arrayBuffer = await event.data.arrayBuffer();

          // Convert ArrayBuffer to Uint8Array
          const audioBytes = new Uint8Array(arrayBuffer);

          if (audioBytes.length === 0) {
            return;
          }

          // Convert to Base64
          const base64Audio = btoa(String.fromCharCode(...audioBytes));

          // Send media event packet to server
          const mediaMessage: MediaEvent = {
            event: StreamEventType.MEDIA,
            streamSid: streamSid,
            sequenceNumber: String(getSequenceNumber()),
            media: {
              payload: base64Audio,
            },
          };

          // Validate streamSid matches current call - drop data from stale/previous calls
          if (streamSid !== streamSIDRef.current) {
            console.warn(
              `Dropping audio chunk from stale call (received: ${streamSid}, current: ${streamSIDRef.current})`,
            );
            return;
          }
          sendMessage(JSON.stringify(mediaMessage));
        } catch (error) {
          console.error('Error occurred. Could not process Blob data', error);
        }
      };

      void handleData(event, streamSid);
    },
    [getSequenceNumber, sendMessage],
  );

  // --- useMediaRecorder ---
  // Old path: onDataAvailable streams audio as base64
  // WebRTC path: onDataAvailable is null (WebRTC handles audio natively)
  // MediaRecorder still runs in both paths for Spectrogram visualization
  const {
    isRecording,
    startRecording,
    stopRecording,
    devices,
    selectedDevice,
    selectDevice,
    mediaRecorder,
    mediaStream,
  } = useMediaRecorder({
    isEnabled: Boolean(
      isEnabled && (connectionToken || draftCallParams !== undefined),
    ),
    onDataAvailable: !isWebRTC ? onDataAvailable : null,
  });

  // Keep mediaStream ref in sync for use in callbacks that can't depend on it directly
  mediaStreamRef.current = mediaStream;

  // --- WebRTC: handle mic device switching via replaceTrack ---
  const prevMediaStreamRef = useRef<MediaStream | null>(null);
  useEffect(() => {
    if (
      !isWebRTC ||
      !mediaStream ||
      mediaStream === prevMediaStreamRef.current
    ) {
      prevMediaStreamRef.current = mediaStream;
      return;
    }
    prevMediaStreamRef.current = mediaStream;
    const audioTrack = mediaStream.getAudioTracks()[0];
    if (audioTrack && isStreaming) {
      void peerConnection.replaceTrack(audioTrack);
    }
  }, [isWebRTC, mediaStream, isStreaming, peerConnection]);

  // --- Reset refs ---
  const resetRefs = useCallback(() => {
    sequenceNumberRef.current = 0;
    streamSIDRef.current = null;
    callSIDRef.current = null;
    callStartTimeRef.current = null;
    marksRef.current = {};
    lastMediaIdRef.current = null;
    sessionIdRef.current = '';
    iceCandidateQueueRef.current = [];
    readyStateRef.current = ReadyState.CLOSED;
  }, []);

  const startCall = useCallback(
    ({ isDraftCall }: { isDraftCall?: boolean } = {}) => {
      resetRefs();
      callStartTimeRef.current = Date.now();
      callAgentIdRef.current = draftCallParams?.projectId ?? '';
      if (!isWebRTC) {
        streamSIDRef.current = getStreamSID();
      }
      callSIDRef.current = getCallSID();
      dispatch(setConversationId(callSIDRef.current));
      setIsDraftCall(Boolean(isDraftCall));
      setHasAgentSpoken(false);
      setIsSocketOpen(true);
    },
    [resetRefs, dispatch, isWebRTC, draftCallParams?.projectId],
  );

  const endCall = useCallback(() => {
    // Immediately close call state to reject any in-flight onDataAvailable callbacks
    readyStateRef.current = ReadyState.CLOSED;

    if (isWebRTC) {
      // Send close message via ref to avoid circular dependency with useWebSocket
      if (sessionIdRef.current) {
        try {
          const closeMsg: CloseMessage = {
            type: SignalingMessageType.CLOSE,
            sessionId: sessionIdRef.current,
          };
          sendWsMessageRef.current(JSON.stringify(closeMsg));
        } catch {
          // WS may already be closed
        }
      }
      peerConnection.close();
      remoteAudio.stopAudio();
    } else {
      // Intentionally reset refs after call ended to avoid staled state
      const playedBytes = stopAudioOld();
      sendMarkMessage(playedBytes);
    }
    stopRecording();

    try {
      if (callStartTimeRef.current) {
        const durationMs = Date.now() - callStartTimeRef.current;
        const durationSeconds = Math.floor(durationMs / 1000);
        trackPendoEvent('call_duration', {
          duration_ms: durationMs,
          duration_seconds: durationSeconds,
          client_env: clientEnv,
          ...(source && { source }),
        });

        const MIN_TEST_CONVERSATION_SECONDS = 30;
        if (durationSeconds >= MIN_TEST_CONVERSATION_SECONDS) {
          trackPendoEvent('test_conversation_succeeded', {
            ...(callAgentIdRef.current && {
              agent_id: callAgentIdRef.current,
            }),
            duration_seconds: durationSeconds,
            ...(source && { source }),
          });
        }
      }
    } catch (e) {
      console.error('Failed to track call duration', e);
    }

    resetRefs();
    setIsDraftCall(false);
    setIsSocketOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isWebRTC,
    resetRefs,
    stopAudioOld,
    sendMarkMessage,
    stopRecording,
    peerConnection,
    remoteAudio,
    clientEnv,
    source,
  ]);

  useEffect(() => {
    // Start recording before starting the call for microphone visual affordance
    if (isEnabled) {
      try {
        // No streamSid yet - this is just for mic visualization before call starts
        void startRecording(null);
      } catch (error) {
        if (error instanceof DOMException) {
          if (
            error.name === 'NotAllowedError' ||
            error.name === 'SecurityError'
          ) {
            toast.error({
              title: 'Failed to get microphone access',
            });
            return;
          }
        }
        toast.error({
          title: 'Failed to start call',
        });
      }
    } else {
      stopRecording();
    }
  }, [toast, isEnabled, startRecording, stopRecording]);

  // Restart mic for waveform visualization after a call ends while panel stays open.
  // Separated from endCall to avoid an async getUserMedia race when closing the panel —
  // effects run after render, so isEnabled is always up-to-date here.
  // Guarded by !isSocketOpen so it doesn't fire during call setup (when
  // webSocketOnOpenOld temporarily stops recording before restarting with a streamSid).
  const wasRecordingRef = useRef(false);
  useEffect(() => {
    const wasRecording = wasRecordingRef.current;
    wasRecordingRef.current = isRecording;
    if (isEnabled && !isRecording && wasRecording && !isSocketOpen) {
      void startRecording(null);
    }
  }, [isEnabled, isRecording, isSocketOpen, startRecording]);

  const setCallClientEnv = useCallback(
    (clientEnv: ClientEnv) => {
      dispatch(setClientEnv(clientEnv));
    },
    [dispatch],
  );

  return {
    isReadyToStream: Boolean(
      (connectionToken || draftCallParams) && selectedDevice,
    ),
    hasConnectionToken: !!(connectionToken || draftCallParams),
    isOpeningStream: readyState === ReadyState.CONNECTING,
    isStreaming: readyState === ReadyState.OPEN,
    isDraftDeploying:
      isDraftCall && readyState === ReadyState.OPEN && !hasAgentSpoken,
    isAgentSpeaking,
    startCall,
    endCall,
    micDevices: devices,
    selectedMicDevice: selectedDevice,
    selectMicDevice: selectDevice,
    setCallClientEnv,
    mediaRecorder,
  };
};

export default useCall;
